import { z } from 'zod'

import { actionItemSchema } from 'shared/api/common/schemas/actionItem'
import {
  InstalledApplicationSchema,
  ApplicationBaseSchema,
  AppAvatarSchema,
} from 'shared/api/common/schemas/applications'
import { sourceItemSchema } from 'shared/api/common/schemas/sourceItem'

import { ConfigErrorSchema } from './errors'

// Applications
const FetchAccountsResponseSchema = z.object({
  account_id: z.string(),
  account_name: z.string(),
  avatar_url: z.string().nullable(),
  app_status: z.number(),
  pro_status: z.boolean(),
})
const AppSettingsSchema = z.object({
  name: z.string(),
  value: z.string().nullable(),
  title: z.string(),
})

const DevApplicationSchema = z.object({
  title: z.string(),
  description: z.string(),
  config: z.looseObject({}).nullable(),
  avatar: AppAvatarSchema,
  api_scope: z.array(z.string()),
})

const DevApplicationWithAppIdSchema = DevApplicationSchema.extend({
  app_id: z.number(),
})

const UpdateSettingsRequestSchema = z.object({
  app_id: z.number(),
  account_id: z.number(),
  settings: z.record(z.string(), z.string().nullable()),
  group_settings: z.array(
    z.object({
      group_id: z.string(),
      title: z.string(),
      settings: z.record(z.string(), z.string().nullable()),
    }),
  ),
})

const UpdateSettingsResponseSchema = z.object({
  settings: z.record(z.string(), z.string().nullable()),
  group_settings: z
    .array(
      z.object({
        group_id: z.string(),
        title: z.string(),
        settings: z.record(z.string(), z.string().nullable()),
      }),
    )
    .optional(),
})

// There is a resynchronization with BE.
// Some fields can be nullable on the BE side by schema, but logically, it's not expected
const GroupAppSettingsSchema = z.object({
  title: z.string(),
  group_id: z.string(),
  settings: z.array(AppSettingsSchema),
})

const AppSettingsResponseSchema = z.object({
  appKey: z.string().nullable(),
  group_settings: z.array(GroupAppSettingsSchema),
  hasRequest: z.boolean(),
  settings: z.array(AppSettingsSchema),
})

// App store

// Account apps
const UsesEntitySchema = z.object({
  id: z.number(),
  title: z.string(),
})

const UsesDictonarySchema = z.object({
  flows: z.array(UsesEntitySchema),
  trigger_rules: z.array(UsesEntitySchema),
  keywords_rules: z.array(UsesEntitySchema),
  integrations: z.array(UsesEntitySchema),
  growth_tools: z.array(UsesEntitySchema),
  sequences: z.array(UsesEntitySchema),
})

const ChangelogItemSchema = z.object({
  version: z.number(),
  changelog: z.string(),
})

export const ApplicationTriggerSchema = z.object({
  app_id: z.number(),
  title: z.string(),
  triggers: z.array(
    z.object({
      name: z.string(),
      title: z.string(),
      description: z.string(),
    }),
  ),
})

// Other

export type ApplicationTrigger = z.infer<typeof ApplicationTriggerSchema>
export type ApplicationAccount = z.infer<typeof FetchAccountsResponseSchema>
export type AppSettings = z.infer<typeof AppSettingsSchema>
export type GroupAppSettings = z.infer<typeof GroupAppSettingsSchema>
export type UsesDictonary = z.infer<typeof UsesDictonarySchema>
export type ChangelogItem = z.infer<typeof ChangelogItemSchema>
export type DevApplication = z.infer<typeof DevApplicationSchema>
export type DevApplicationWithAppId = z.infer<typeof DevApplicationWithAppIdSchema>
export type UpdateSettingsRequest = z.infer<typeof UpdateSettingsRequestSchema>

export const ApplicationsSchemas = {
  // Applications
  fetchAccounts: {
    path: z.undefined(),
    query: z.object({ app_id: z.number() }),
    request: z.undefined(),
    response: z.object({ accounts: z.array(FetchAccountsResponseSchema) }),
  },
  fetchSettingsForm: {
    path: z.undefined(),
    query: z.object({
      account_id: z.string().or(z.number()),
      app_id: z.string().or(z.number()),
    }),
    request: z.undefined(),
    response: AppSettingsResponseSchema,
  },
  fetchList: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({ apps: z.array(ApplicationBaseSchema) }),
  },
  fetchItem: {
    path: z.undefined(),
    query: z.object({ app_id: z.number() }),
    request: z.undefined(),
    response: z.object({ app: DevApplicationWithAppIdSchema }),
  },
  createApplication: {
    path: z.undefined(),
    query: z.undefined(),
    request: DevApplicationSchema,
    response: z.object({ app: DevApplicationWithAppIdSchema }),
    errors: {
      ConfigError: ConfigErrorSchema,
    },
  },
  updateApplication: {
    path: z.undefined(),
    query: z.undefined(),
    request: DevApplicationWithAppIdSchema,
    response: z.object({ app: DevApplicationWithAppIdSchema }),
    errors: {
      ConfigError: ConfigErrorSchema,
    },
  },
  uninstallApplication: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({ app_id: z.number(), account_id: z.number() }),
    response: z.undefined(),
  },
  publishApplication: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      app_id: z.number(),
      changelog: z.string().nullable(),
    }),
    response: z.undefined(),
  },
  updateSettings: {
    path: z.undefined(),
    query: z.undefined(),
    request: UpdateSettingsRequestSchema,
    response: UpdateSettingsResponseSchema,
  },
  getReport: {
    path: z.undefined(),
    query: z.object({
      app_id: z.number(),
      version: z.number(),
    }),
    request: z.undefined(),
    response: z.object({
      report: z.string(),
    }),
  },
  uploadAvatar: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.instanceof(FormData),
    response: z.object({ avatar: z.string() }),
  },

  // App store
  fetchApplicationByHash: {
    path: z.undefined(),
    query: z.object({ app_hash: z.string() }),
    request: z.undefined(),
    response: z.object({ app: InstalledApplicationSchema, hasSettings: z.boolean() }),
  },
  installApplication: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({ account_id: z.string(), app_hash: z.string() }),
    response: z.undefined(),
  },

  // Account apps
  fetchInstalledApps: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({
      apps: z.array(InstalledApplicationSchema),
    }),
  },
  fetchUsesList: {
    path: z.undefined(),
    query: z.object({
      app_id: z.string().or(z.number()),
    }),
    request: z.undefined(),
    // Note: We are using only part of the data structure in the application.
    // We defined only the necessary fields and entities.
    response: z.object({
      entities: UsesDictonarySchema,
    }),
  },
  getChangelogs: {
    path: z.undefined(),
    query: z.object({
      app_id: z.string().or(z.number()),
    }),
    request: z.undefined(),
    response: z.object({
      changelog: z.array(ChangelogItemSchema),
    }),
  },
  updateVersion: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.object({
      app_id: z.string().or(z.number()),
    }),
    response: z.undefined(),
  },
  getAppAction: {
    path: z.undefined(),
    query: z.object({ app_id: z.number(), action_name: z.string() }),
    request: z.undefined(),
    response: z.object({ action: actionItemSchema }),
  },
  loadAppSource: {
    path: z.undefined(),
    query: z.object({
      app_id: z.number(),
      source_name: z.string(),
      group: z.string().or(z.number()).optional(),
    }),
    request: z.undefined(),
    response: z.object({ source: z.array(sourceItemSchema) }),
  },
  fetchAppTriggers: {
    path: z.undefined(),
    query: z.undefined(),
    request: z.undefined(),
    response: z.object({ apps: z.array(ApplicationTriggerSchema) }),
  },
  getAppActions: {
    path: z.undefined(),
    query: z.object({ app_id: z.number() }),
    request: z.undefined(),
    response: z.object({
      actions: z.array(
        z
          .object({
            title: z.string(),
            name: z.string(),
            description: z.string(),
          })
          .or(z.void()),
      ),
    }),
  },
}
